import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, Platform } from 'react-native';

import { useAppContext } from '../../shared/contexts/AppContext';
import { authService } from '../../shared/services/authService';

import OnboardingFlow from './OnboardingFlow';

type OnboardingStep =
  | 'welcome'
  | 'completed';

interface OnboardingManagerProps {
  onComplete: () => void;
}

interface OnboardingProgress {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  skippedSteps: OnboardingStep[];
  startedAt: string;
  completedAt?: string;
  userChoices: Record<string, unknown>;
}

const ONBOARDING_STORAGE_KEY = '@kingdom_quiz_onboarding';
const ONBOARDING_COMPLETED_KEY = '@kingdom_quiz_onboarding_completed';

const OnboardingManager: React.FC<OnboardingManagerProps> = ({ onComplete }) => {
  const { state } = useAppContext();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [progress, setProgress] = useState<OnboardingProgress>({
    currentStep: 'welcome',
    completedSteps: [],
    skippedSteps: [],
    startedAt: new Date().toISOString(),
    userChoices: {}
  });

  useEffect(() => {
    loadOnboardingProgress();
  }, []);

  const loadOnboardingProgress = async () => {
    try {
      const savedProgress = await AsyncStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (savedProgress) {
        const parsedProgress: OnboardingProgress = JSON.parse(savedProgress);
        setProgress(parsedProgress);
        setCurrentStep(parsedProgress.currentStep);
      }
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
    }
  };

  const saveOnboardingProgress = async (newProgress: OnboardingProgress) => {
    try {
      await AsyncStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(newProgress));
      setProgress(newProgress);
    } catch (error) {
      console.error('Failed to save onboarding progress:', error);
    }
  };

  const markOnboardingCompleted = async () => {
    console.log('markOnboardingCompleted called');
    try {
      console.log('Setting AsyncStorage items...');
      await AsyncStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
      await AsyncStorage.removeItem(ONBOARDING_STORAGE_KEY);
      console.log('AsyncStorage items set successfully');

      const finalProgress: OnboardingProgress = {
        ...progress,
        currentStep: 'completed',
        completedAt: new Date().toISOString()
      };

      // Track onboarding completion analytics
      console.log('Tracking onboarding completion...');
      trackOnboardingCompletion(finalProgress);

      // Complete any pending referrals
      if (state.user?.id) {
        console.log('Completing user referrals...');
        await authService.completeUserReferrals(state.user.id);
      }

      console.log('About to call onComplete callback');
      console.log('onComplete type:', typeof onComplete);
      onComplete();
      console.log('onComplete called successfully');
    } catch (error) {
      console.error('Failed to mark onboarding as completed:', error);
      console.log('Calling onComplete anyway due to error');
      onComplete(); // Complete anyway
    }
  };

  const trackOnboardingCompletion = (finalProgress: OnboardingProgress) => {
    // TODO: Send analytics to track onboarding success
    const analytics = {
      duration: new Date().getTime() - new Date(finalProgress.startedAt).getTime(),
      completedSteps: finalProgress.completedSteps.length,
      skippedSteps: finalProgress.skippedSteps.length
    };

    console.log('Onboarding completed:', analytics);
  };

  const moveToNextStep = (completed: boolean = true) => {
    // Simplified onboarding - only welcome flow, then complete
    const stepOrder: OnboardingStep[] = [
      'welcome',
      'completed'
    ];

    const currentIndex = stepOrder.indexOf(currentStep);
    const nextStep = stepOrder[currentIndex + 1];

    if (!nextStep || nextStep === 'completed') {
      markOnboardingCompleted();
      return;
    }

    const newProgress: OnboardingProgress = {
      ...progress,
      currentStep: nextStep,
      completedSteps: completed
        ? [...progress.completedSteps, currentStep]
        : progress.completedSteps,
      skippedSteps: !completed
        ? [...progress.skippedSteps, currentStep]
        : progress.skippedSteps
    };

    setCurrentStep(nextStep);
    saveOnboardingProgress(newProgress);
  };

  const handleWelcomeComplete = () => {
    // Skip directly to completion after welcome flow
    markOnboardingCompleted();
  };

  // Check if user should see onboarding
  const shouldShowOnboarding = async (): Promise<boolean> => {
    try {
      const completed = await AsyncStorage.getItem(ONBOARDING_COMPLETED_KEY);
      return completed !== 'true';
    } catch (error) {
      return true; // Show onboarding if we can't determine status
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <OnboardingFlow onComplete={handleWelcomeComplete} />
        );

      default:
        return null;
    }
  };

  return (
    <View style={styles.container}>
      {renderCurrentStep()}
    </View>
  );
};

// Utility functions for onboarding management
export const shouldShowOnboarding = async (): Promise<boolean> => {
  try {
    const completed = await AsyncStorage.getItem(ONBOARDING_COMPLETED_KEY);
    return completed !== 'true';
  } catch (error) {
    return true;
  }
};

export const resetOnboarding = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    await AsyncStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to reset onboarding:', error);
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default OnboardingManager;
